import styled from 'styled-components';
import Link from '~/components/common/link';
import { color, spacing, media, typography } from '~/theme';
import { TextParagraph } from '~/theme/elements';
import { rem } from '~/theme/variables/typography';

export const SubHeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    font-weight: ${typography.fontWeightBold};
    color: ${color.palette.white};
    a {
        text-decoration: none;
        color: inherit;
    }
`;

export const SubHeaderHeading = styled.p`
    font-size: ${rem(typography.fontSizeExtraLarge + 4)};
    font-family: ${typography.fontSecondary};
    line-height: ${typography.lineHeight16};
    letter-spacing: ${typography.letterSpacingLarge};
    margin: ${spacing.spacing0};
    padding-top: ${spacing.spacing0};
    padding-bottom: calc(${spacing.spacing1} / 2);
`;

export const SubHeaderDescription = styled.p`
    font-size: ${rem(typography.fontSizeLarge - 1)};
    font-family: ${typography.fontSecondary};
    line-height: ${typography.lineHeight18};
    letter-spacing: ${typography.letterSpacingSmall};
    margin: ${spacing.spacing0};
    padding-top: calc(${spacing.spacing1} / 2);
    padding-bottom: ${spacing.spacing1};
`;

export const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
`;

export const InboxButton = styled.button`
    border: none;
    border-radius: ${spacing.spacing1};
    cursor: pointer;
    background-color: ${color.palette.serviceOntarioGreen};
    height: 48px;
    margin-right: ${spacing.spacing1};

    &:focus,
    &:active {
        box-shadow: 0 0 0 4px ${color.focusBlue};
        outline: 4px solid transparent;
        transition: box-shadow 0.1s ease-in-out;
    }
`;

export const UnreadCounter = styled.span`
    background-color: ${color.palette.systemDarkRed};
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${typography.fontSizeSmall}px;
    color: ${color.palette.white};
    position: relative;
    right: ${spacing.spacing5};
    bottom: ${spacing.spacing2};
    height: 17px;
    width: 17px;
    border: ${color.palette.serviceOntarioGreen} 1px solid;
`;

export const InboxList = styled.ul`
    margin: ${spacing.spacing0};
    padding: ${spacing.spacing3} ${spacing.spacing0};
    background: ${color.palette.white};
    border-radius: ${spacing.spacing1};
    box-shadow: 3px 4px 4px rgba(0, 0, 0, 0.25);
    list-style: none;
    & li:last-child {
        border-bottom: 1px solid ${color.palette.greyscale20};
    }
`;

export const InboxHeadingContainer = styled.div`
    font-size: ${typography.defaultFontSize};
    padding-left: ${spacing.spacing5};
    width: 352px;
`;

export const StyledMessageLink = styled(Link)`
    text-decoration: none;
    color: ${color.palette.black};
    display: block;

    &:hover,
    &:visited {
        color: ${color.palette.black};
    }
`;

export const InboxListItem = styled.li<{ isRead?: boolean }>`
    border-top: 1px solid ${color.palette.greyscale20};
    padding: ${spacing.spacing4} calc(${spacing.spacing5} - 3px);
    gap: ${spacing.spacing7};
    max-width: 352px;

    &:hover {
        cursor: pointer;
        background: ${color.palette.greyscale5};
    }

    &::marker {
        color: ${color.palette.systemFocusBlue};
        content: ${p => (p.isRead ? 'none' : 'unset')};
    }

    svg {
        margin: ${spacing.spacing3} ${spacing.spacing2} ${spacing.spacing0} ${spacing.spacing2};
        width: 24px;
        height: 24px;
    }
`;

export const InboxListItemContainer = styled.div<{ isRead: boolean }>`
    display: inline-flex;
    padding-left: ${p => (p.isRead ? `calc(${spacing.spacing5} - 2px)` : `${spacing.spacing0}`)};
`;

export const MessageContainer = styled.div<{ isRead: boolean }>`
    display: flex;
    flex-direction: column;
    padding: ${spacing.spacing0} ${spacing.spacing2} ${spacing.spacing0}
        ${p => (p.isRead ? spacing.spacing5 : spacing.spacing3)};
`;

export const MessageSubject = styled(TextParagraph)<{ isRead: boolean }>`
    font-size: calc(${typography.fontSizeLarge}px - 1px);
    display: inline;
    margin: ${spacing.spacing0};
    font-weight: ${p =>
        p.isRead ? `${typography.fontWeightRegular}` : `${typography.fontWeightBold}`};
`;

export const MessageBody = styled(TextParagraph)`
    font-size: ${typography.defaultFontSize}px;
    margin: ${spacing.spacing0};

    text-overflow: ellipsis;
    overflow: hidden;
    width: 254px;
    white-space: nowrap;
`;

export const MessageDate = styled.p`
    font-size: calc(${typography.fontSizeSmall} - 1px);
    margin: ${spacing.spacing2} ${spacing.spacing0} ${spacing.spacing0};
    color: ${color.palette.greyscale60};
`;

export const MenuButton = styled.button`
    align-items: center;
    background-color: ${color.palette.serviceOntarioGreen};
    border-radius: ${spacing.spacing1};
    color: ${color.palette.white};
    cursor: pointer;
    display: flex;
    font-size: 1.125rem;
    border: none;
    font-weight: ${typography.fontWeightSemiBold};
    height: ${spacing.spacing8};
    text-align: center;
    padding: ${spacing.spacing4} ${spacing.spacing3};
    svg {
        position: inherit;
    }

    &:focus,
    &:active {
        box-shadow: 0 0 0 4px ${color.focusBlue};
        outline: 4px solid transparent;
        transition: box-shadow 0.1s ease-in-out;
    }
`;

export const StyledName = styled.p`
    margin: 0;
    display: none;

    @media (${media.small}) {
        display: unset;
    }
`;

export const MenuList = styled.ul`
    list-style: none;
    margin: ${spacing.spacing0};
    padding: ${spacing.spacing3} ${spacing.spacing0};
    background: ${color.palette.white};
    border-radius: ${spacing.spacing1};
    box-shadow: 3px 4px 4px rgba(0, 0, 0, 0.25);
    & li:last-child {
        border-bottom: 1px solid ${color.palette.greyscale20};
    }
`;

export const MenuListItem = styled.li`
    border-top: 1px solid ${color.palette.greyscale20};
    padding: ${spacing.spacing4};
    display: flex;
    align-items: center;
    gap: ${spacing.spacing2};
    cursor: pointer;

    &:hover {
        background: ${color.palette.greyscale5};
    }

    svg {
        width: 24px;
        height: 24px;
    }
`;

export const MenuItemLink = styled(Link)`
    color: ${color.palette.black};
    padding-left: calc(${spacing.spacing2} + 2px);
    font-size: 1.125rem;
    font-weight: ${typography.fontWeightSemiBold};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
        color: ${color.palette.black};
    }

    &:visited {
        color: ${color.palette.black};
    }
`;

export const AppHeaderContainer = styled.div`
    display: flex;
    align-items: center;
    background-color: ${color.palette.serviceOntarioGreen};
    min-height: ${rem(84)};

    @media (${media.small}) {
        min-height: ${rem(92)};
    }
`;

export const AppHeaderInnerContainer = styled.div`
    width: 100%;
    max-width: 22.625rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${spacing.spacing0} ${spacing.spacing4};
    margin: 0 auto;
    position: relative;

    @media (${media.small}) {
        max-width: 48rem;
    }

    @media (${media.medium}) {
        max-width: 72rem;
    }
`;
